// @ts-nocheck
import { useState } from 'react';
import { Stack, Title, Text as MantineText, Code, SimpleGrid } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { Card } from '../../../components/ui';
import { EventCalendar } from '../../../components/advanced';

export default function CalendarPage() {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  const mockEvents = [
    {
      id: '1',
      title: 'Team Standup',
      start: new Date(2024, 0, 15, 9, 0),
      end: new Date(2024, 0, 15, 9, 30),
      color: 'blue',
    },
    {
      id: '2',
      title: 'Client Meeting',
      start: new Date(2024, 0, 15, 14, 0),
      end: new Date(2024, 0, 15, 15, 30),
      color: 'green',
    },
    {
      id: '3',
      title: 'Project Review',
      start: new Date(2024, 0, 16, 10, 0),
      end: new Date(2024, 0, 16, 11, 0),
      color: 'orange',
    },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Calendar Components</Title>
        <MantineText c="dimmed" size="sm">
          Calendar and event scheduling components for managing dates and appointments
        </MantineText>
      </Stack>

      {/* Basic Calendar */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Calendar</Title>
            <MantineText size="sm" c="dimmed">
              Interactive date picker calendar with selection support
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <div style={{ maxWidth: 400 }}>
              <Calendar
                value={selectedDate}
                onChange={setSelectedDate}
              />
            </div>
            {selectedDate && (
              <MantineText size="sm" c="dimmed">
                Selected: {selectedDate.toLocaleDateString()}
              </MantineText>
            )}
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

<Calendar
  value={selectedDate}
  onChange={setSelectedDate}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • value - Selected date
              <br />
              • onChange - Date change handler
              <br />
              • minDate / maxDate - Date range restrictions
              <br />
              • excludeDate - Function to exclude specific dates
              <br />
              • firstDayOfWeek - 0 (Sunday) or 1 (Monday)
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Event Calendar */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>EventCalendar</Title>
            <MantineText size="sm" c="dimmed">
              Full-featured calendar with event display and scheduling
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <EventCalendar
              events={mockEvents}
              onEventClick={(event) => console.log('Event clicked:', event)}
              onDateClick={(date) => console.log('Date clicked:', date)}
              onAddEvent={(date) => console.log('Add event on:', date)}
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`const events = [
  {
    id: '1',
    title: 'Team Standup',
    start: new Date(2024, 0, 15, 9, 0),
    end: new Date(2024, 0, 15, 9, 30),
    color: 'blue',
  },
  // ... more events
];

<EventCalendar
  events={events}
  onEventClick={(event) => console.log('Event clicked:', event)}
  onDateClick={(date) => console.log('Date clicked:', date)}
  onAddEvent={(date) => console.log('Add event on:', date)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Event Interface:</MantineText>
            <Code block language="typescript">
{`interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color?: string;
  description?: string;
  location?: string;
  attendees?: string[];
  allDay?: boolean;
}`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • events - Array of calendar events
              <br />
              • onEventClick - Event click handler
              <br />
              • onDateClick - Date cell click handler
              <br />
              • onAddEvent - Add event handler
              <br />
              • view - 'month' | 'week' | 'day' | 'agenda'
              <br />
              • onViewChange - View change handler
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Meeting Scheduler</MantineText>
              <MantineText size="xs" c="dimmed">
                Display team meetings, appointments, and availability. Allow users to schedule new meetings by clicking on available time slots.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Project Timeline</MantineText>
              <MantineText size="xs" c="dimmed">
                Visualize project milestones, deadlines, and task schedules. Track progress and identify conflicts.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Event Management</MantineText>
              <MantineText size="xs" c="dimmed">
                Manage company events, conferences, and team activities. Show event details and RSVP status.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Resource Booking</MantineText>
              <MantineText size="xs" c="dimmed">
                Book meeting rooms, equipment, or other shared resources. View availability and prevent double-booking.
              </MantineText>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
